/**
 * Project status mapping utility
 */

export interface ProjectStatusOption {
  id: string;
  name: string;
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  category?: string;
}

const PROJECT_STATUS_OPTIONS: ProjectStatusOption[] = [
  { id: "planned", name: "Planned", color: "default", category: "Planning" },
  { id: "ongoing", name: "Ongoing", color: "success", category: "Active" },
  { id: "cancelled", name: "Cancelled", color: "error", category: "Inactive" },
  {
    id: "completed",
    name: "Completed",
    color: "primary",
    category: "Completed",
  },
  { id: "at_risk", name: "At Risk", color: "warning", category: "Risk" },
  { id: "proposed", name: "Proposed", color: "default", category: "Planning" },
];

export const projectStatusMapping = {
  /**
   * Get all project status options
   */
  getAllOptions: (): ProjectStatusOption[] => {
    return PROJECT_STATUS_OPTIONS;
  },

  /**
   * Search project statuses by name (case-insensitive)
   */
  searchByName: (searchTerm: string): ProjectStatusOption[] => {
    if (!searchTerm.trim()) {
      return PROJECT_STATUS_OPTIONS;
    }

    const term = searchTerm.toLowerCase();
    return PROJECT_STATUS_OPTIONS.filter(
      (status) =>
        status.name.toLowerCase().includes(term) ||
        status.id.toLowerCase().includes(term)
    );
  },

  /**
   * Get project status by ID
   */
  getById: (id: string): ProjectStatusOption | undefined => {
    return PROJECT_STATUS_OPTIONS.find((status) => status.id === id);
  },

  /**
   * Get display name for project status ID
   */
  getDisplayName: (id: string): string => {
    const status = PROJECT_STATUS_OPTIONS.find((s) => s.id === id);
    return status?.name || id;
  },

  /**
   * Get color for project status ID (for Material-UI Chip component)
   */
  getStatusColor: (
    id: string
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    const status = PROJECT_STATUS_OPTIONS.find((s) => s.id === id);
    return status?.color || "default";
  },

  /**
   * Get category for project status ID
   */
  getCategory: (id: string): string | undefined => {
    const status = PROJECT_STATUS_OPTIONS.find((s) => s.id === id);
    return status?.category;
  },

  /**
   * Get all statuses by category
   */
  getStatusesByCategory: (category: string): ProjectStatusOption[] => {
    return PROJECT_STATUS_OPTIONS.filter((s) => s.category === category);
  },

  /**
   * Get all unique categories
   */
  getCategories: (): string[] => {
    const categories = PROJECT_STATUS_OPTIONS.map((s) => s.category).filter(
      Boolean
    ) as string[];
    return Array.from(new Set(categories));
  },
};
